import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaCamera, FaCheckCircle, FaClipboardList, FaClock, FaComments, FaDirections, FaMapMarkerAlt, FaPhoneAlt, FaStickyNote, FaUndoAlt } from 'react-icons/fa'
import { Banner, Bar, ChipSelect, ChoiceCards, ConfirmSheet, Field, FoBtn, KV, Pill, ScreenShell, Section, Sheet, TextArea } from './fieldUi'
import { FarForm, LinesTable, PhotoStrip, ReturnsMini, minutesLabel, useNow } from './shared'
import NearbyPanel from './NearbyPanel'
import { positionAtCheckIn, useField } from './fieldContext'
import { storeInfo, storeMap, proximity } from '../store'
import { clockOfStamp, fmtMiles, fmtMinutes, mapsNavigateUrl, roadMiles, driveMinutes, telUrl } from '../geo'
import { CLOSED_REASONS, NO_ORDER_REASONS } from '../demoData'
import { fmtDate, fmtShort, money, money2 } from '../FieldOpsKit'
import { VISIT_LABEL, VISIT_TONE } from './stops'

const FAR_REASONS = ['Parking is further away', 'The store moved', 'GPS is inaccurate here', 'Other']
const MANUAL_REASONS = ['Location is off on my phone', 'No GPS signal here', 'Other']

/**
 * One store, three ways: before the visit (check-in), during it (actions + result) and after it (summary).
 * `id` = visit id; `storeId` = open the store profile without a visit (Stores tab); `readOnly` = from History.
 */
export default function StopScreen({ id, storeId, readOnly, autoCheckin }) {
    const f = useField()
    const { state, nav, actor, gps, date } = f
    const field = state.field
    const visit = id ? state.sales.visits.filter((v) => v.id === id)[0] : null
    const sMap = useMemo(() => storeMap(state.sales), [state.sales])
    const store = visit ? sMap[visit.storeId] : sMap[storeId]
    const info = useMemo(() => (store ? storeInfo(state.sales, store.id, f.vIdx, f.oIdx) : null), [state.sales, store ? store.id : '']) // eslint-disable-line react-hooks/exhaustive-deps
    const now = useNow(30000)
    const [result, setResult] = useState('')
    const [reason, setReason] = useState('')
    const [notes, setNotes] = useState(visit ? visit.notes || '' : '')
    const [tried, setTried] = useState(false)
    const [far, setFar] = useState(null)       // pending far-from-store check-in { pos, prox }
    const [manual, setManual] = useState(false)
    const [cancel, setCancel] = useState(false)
    const [noteOpen, setNoteOpen] = useState(false)
    const [saved, setSaved] = useState(false)
    const [thumbs, setThumbs] = useState([])
    const fileRef = useRef(null)
    const autoRan = useRef(false)

    const order = visit ? state.sales.orders.filter((o) => o.visitId === visit.id)[0] || null : null
    const status = visit ? visit.status : 'none'
    const isToday = visit ? visit.date === date : false
    const otherCurrent = f.stops.filter((s) => s.state === 'current' && (!visit || s.id !== visit.id))[0] || null
    const prox = store && gps.pos ? proximity(gps.pos, store) : null

    // "Check in" pressed on the route card: start the check-in as soon as this screen opens (once)
    useEffect(() => {
        if (autoRan.current || !autoCheckin || !visit || status !== 'planned' || readOnly) return
        autoRan.current = true
        startCheckIn()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (!store) return <ScreenShell title="Store not found" onBack={nav.pop}><Banner tone="warn">This store is not available.</Banner></ScreenShell>

    /* ---------------- check-in ---------------- */
    const commitCheckIn = (pos, extra) => {
        f.send(Object.assign({ type: 'VISIT_CHECKIN2', visitId: visit.id, actorKey: actor.key, date: visit.date, lat: pos ? pos.lat : store.lat, lng: pos ? pos.lng : store.lng, distMi: pos ? proximity(pos, store).distMi : null, far: false }, extra || {}))
        f.toast('Checked in at ' + store.name + ' · ' + clockOfStamp(Date.now()))
        setFar(null); setManual(false)
    }
    function startCheckIn() {
        if (!visit) return
        if (otherCurrent) { f.toast('Finish your visit at ' + otherCurrent.name + ' first.', 'warn'); return }
        if (!isToday) { f.toast('You can only check in on the day of the visit.', 'warn'); return }
        const pos = positionAtCheckIn(field, store, gps.pos)
        if (!pos) { setManual(true); return }
        const p = proximity(pos, store)
        if (p.far) { setFar({ pos: pos, prox: p }); return }
        commitCheckIn(pos)
    }

    /* ---------------- finish ---------------- */
    const effectiveResult = order ? 'order' : result
    const canFinish = status === 'in_progress' && (effectiveResult === 'order' || ((effectiveResult === 'no_order' || effectiveResult === 'closed') && reason))
    const finish = () => {
        setTried(true)
        if (!canFinish) { f.toast(effectiveResult ? 'Pick the reason first.' : 'Choose the result of the visit first.', 'warn'); return }
        f.dispatch({ type: 'VISIT_COMPLETE', visitId: visit.id, outcome: effectiveResult, reason: effectiveResult === 'order' ? '' : reason, notes: notes })
        setSaved(true)
    }
    const nextAfter = f.stops.filter((s) => s.state === 'pending' && (!visit || s.id !== visit.id))[0] || null

    const onPhoto = (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        f.dispatch({ type: 'VISIT_PHOTO', visitId: visit.id })
        setThumbs(thumbs.concat([URL.createObjectURL(file)]))
        f.toast('Photo attached.')
        e.target.value = ''
    }
    const messageOffice = (text) => {
        f.send({ type: 'CHAT_SEND', actorKey: actor.key, from: 'worker', by: actor.name, text: text, topic: 'general', ref: { type: 'store', id: store.id, label: store.name } })
        f.toast('Message sent to the office.')
    }
    const addToRoute = (where) => {
        f.dispatch({ type: 'VISIT_EXTRA', storeId: store.id, rep: actor.rep, date: date, source: 'manual', nextUp: where === 'next' })
        f.toast(store.name + ' added to today’s route.')
        nav.pop()
    }

    /* ---------------- pieces ---------------- */
    const goalPct = info && info.goal ? Math.min(100, Math.round((info.sold / info.goal) * 100)) : 0
    const snapshot = (
        <Section title="Store" right={info && info.state ? <Pill tone={info.state.tone}>{info.state.label}</Pill> : null}>
            {store.hold ? <Banner tone="danger" title="Credit hold.">Past due balance {money2(store.balance)}. New orders need office approval.</Banner> : (store.balance > 0 ? <Banner tone="warn">Open balance {money2(store.balance)} — remind the owner if it comes up.</Banner> : null)}
            <KV label="Address">{store.address}, {store.city}</KV>
            <KV label="Contact">{store.contact}</KV>
            <KV label="Hours">{store.hours}</KV>
            <KV label="Route">{store.route} · {store.rep}</KV>
            {info && info.goal ? (
                <div className="fo-goal">
                    <div className="fo-goal__top"><span>Goal this month</span><strong>{money(info.sold)} <small>of {money(info.goal)}</small></strong></div>
                    <Bar value={info.sold} max={info.goal} tone={info.state && info.state.key === 'behind' ? 'amber' : info.state && info.state.key === 'met' ? 'green' : 'blue'} />
                    <div className="fo-goal__hint">{goalPct >= 100 ? 'Goal reached — anything else is extra.' : info.gap > 0 ? 'Needs ' + money(info.gap) + ' more to reach the goal.' : ''}</div>
                </div>
            ) : <KV label="Goal this month">Not published</KV>}
            <KV label="Last visit">{info && info.lastVisit ? fmtShort(info.lastVisit.date) + ' · ' + (VISIT_LABEL[info.lastVisit.outcome] || '') + (info.daysSince !== null ? ' (' + info.daysSince + ' days ago)' : '') : 'Never visited'}</KV>
            <KV label="Last order">{info && info.lastOrder ? fmtShort(info.lastOrder.date) + ' · ' + money(info.lastOrder.total) : 'No orders yet'}</KV>
            <KV label="Average order">{info && info.avgOrder ? money(info.avgOrder) : '—'}</KV>
            {(state.sales.notes[store.id] || []).slice(-2).map((n) => <div key={n.id} className="fo-note">{n.text}<small>{n.by} · {fmtShort(new Date(n.at).toISOString().slice(0, 10))}</small></div>)}
        </Section>
    )

    const historyBlock = info ? (
        <Section title="Recent visits & orders">
            {info.visits.filter((v) => v.status === 'done' || v.status === 'missed').sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5).map((v) => (
                <KV key={v.id} label={fmtShort(v.date)}>{v.status === 'missed' ? 'Missed' + (v.missReason ? ' · ' + v.missReason : '') : (VISIT_LABEL[v.outcome] || 'Visit') + (v.reason ? ' · ' + v.reason : '')}</KV>
            ))}
            {info.visits.filter((v) => v.status === 'done' || v.status === 'missed').length === 0 ? <div className="fo-muted">No visits yet.</div> : null}
        </Section>
    ) : null

    const header = (
        <div className="fo-storehead">
            <div><strong>{store.name}</strong><span>{store.address}, {store.city}</span></div>
            <div className="fo-storehead__act">
                <FoBtn kind="ghost" small icon={<FaDirections />} href={mapsNavigateUrl(store.lat, store.lng)} newTab>Navigate</FoBtn>
                <FoBtn kind="ghost" small icon={<FaPhoneAlt />} href={telUrl(store.phone)}>{store.phone}</FoBtn>
            </div>
        </div>
    )

    /* ---------------- states ---------------- */
    let body = null
    let footer = null
    const title = store.name
    let subtitle = visit ? (status === 'in_progress' ? 'Visit in progress' : status === 'done' ? 'Visit finished' : status === 'missed' ? (visit.skipped ? 'Skipped' : 'Missed') : 'Planned visit') + ' · ' + fmtDate(visit.date) : 'Store profile'

    if (!visit) {
        // store profile
        body = (
            <React.Fragment>
                {header}
                {snapshot}
                {historyBlock}
                {(info ? info.orders : []).slice(0, 3).map((o) => (
                    <Section key={o.id} title={(o.soNo || 'Order') + ' · ' + fmtShort(o.date)} right={<strong>{money2(o.total)}</strong>}><LinesTable lines={o.lines} /></Section>
                ))}
            </React.Fragment>
        )
        const inToday = f.stops.some((s) => s.kind === 'visit' && s.store.id === store.id)
        footer = inToday ? <FoBtn kind="ghost" onClick={nav.pop}>Already in today’s route</FoBtn> : (
            <React.Fragment>
                <FoBtn kind="soft" onClick={() => addToRoute('end')} disabled={!f.isToday}>Add at the end</FoBtn>
                <FoBtn kind="primary" onClick={() => addToRoute('next')} disabled={!f.isToday}>Visit next</FoBtn>
            </React.Fragment>
        )
    } else if (status === 'planned' && !readOnly) {
        const eta = f.stops.filter((s) => s.id === visit.id)[0]
        body = (
            <React.Fragment>
                {header}
                {otherCurrent ? <Banner tone="warn" title="You are still at another store.">Finish your visit at {otherCurrent.name} before starting this one.</Banner> : null}
                {!isToday ? <Banner tone="info">This visit is planned for {fmtDate(visit.date)}. You can check in that day.</Banner> : null}
                {gps.source === 'denied' ? <Banner tone="warn">Location is off. You can still check in manually.</Banner> : prox ? (
                    <div className="fo-prox">{prox.far ? <FaMapMarkerAlt style={{ color: '#D97706' }} /> : <FaCheckCircle style={{ color: '#059669' }} />} <span>You are about <strong>{fmtMiles(roadMiles(gps.pos, store))}</strong> from this store (~{fmtMinutes(driveMinutes(roadMiles(gps.pos, store)))} by car).</span></div>
                ) : null}
                {eta && eta.eta ? <div className="fo-prox"><FaClock style={{ color: '#2563EB' }} /> <span>Expected arrival <strong>{fmtEtaClock(eta.eta.arrive)}</strong></span></div> : null}
                {snapshot}
                {historyBlock}
                <Section title="Nearby stores worth a stop" tight><NearbyPanel anchor={{ lat: store.lat, lng: store.lng }} limit={3} compact /></Section>
            </React.Fragment>
        )
        footer = (
            <React.Fragment>
                <FoBtn kind="primary" big onClick={startCheckIn} disabled={!!otherCurrent || !isToday}>Check in</FoBtn>
            </React.Fragment>
        )
    } else if (status === 'in_progress' && !readOnly) {
        const mins = Math.max(0, Math.round((now - visit.checkInAt) / 60000))
        body = (
            <React.Fragment>
                <div className="fo-timer">
                    <div style={{ flex: 1 }}><strong>{minutesLabel(mins)}</strong><span>on site since {clockOfStamp(visit.checkInAt)}</span></div>
                    {visit.pending ? <Pill tone="amber">To send</Pill> : <Pill tone="cyan">Checked in</Pill>}
                </div>
                {mins >= 120 ? <Banner tone="warn" title="Long visit.">You have been checked in for {minutesLabel(mins)}. If you already left, finish the visit so the time is not counted against you.</Banner> : null}
                {visit.far ? <Banner tone="warn" title="Far from the store.">Checked in {visit.distMi ? fmtMiles(visit.distMi) : ''} away. Reason: {visit.farReason || '—'}</Banner> : null}
                {visit.manual ? <Banner tone="info">Manual check-in (no location).</Banner> : null}
                {store.hold ? <Banner tone="danger" title="Credit hold.">Past due {money2(store.balance)}. Orders need office approval — ask them before you write one.</Banner> : null}

                <Section title="What do you want to do?">
                    <div className="fo-actions-grid">
                        <button type="button" className={'fo-tile' + (order ? ' is-done' : ' fo-tile--primary')} onClick={() => nav.push({ type: 'order', visitId: visit.id })} disabled={store.hold && !order}>
                            <FaClipboardList /><strong>{order ? 'Order saved' : 'New Sales Order'}</strong><span>{order ? (order.soNo || 'Pending sync') + ' · ' + money2(order.total) : store.hold ? 'Blocked by credit hold' : 'Build the order for this store'}</span>
                        </button>
                        <button type="button" className="fo-tile" onClick={() => nav.push({ type: 'return', source: 'visit', refId: visit.id })}><FaUndoAlt /><strong>Return / credit</strong><span>Damaged, expired, wrong item…</span></button>
                        <button type="button" className="fo-tile" onClick={() => fileRef.current && fileRef.current.click()}><FaCamera /><strong>Take a photo</strong><span>{visit.photos ? visit.photos + ' attached' : 'Shelf, display, problem'}</span></button>
                        <button type="button" className="fo-tile" onClick={() => setNoteOpen(true)}><FaStickyNote /><strong>Store note</strong><span>Shared with the next visit</span></button>
                        <button type="button" className="fo-tile" onClick={() => messageOffice('About ' + store.name + ': ')}><FaComments /><strong>Message the office</strong><span>Price, complaint, change…</span></button>
                        <a className="fo-tile" href={telUrl(store.phone)}><FaPhoneAlt /><strong>Call the store</strong><span>{store.phone}</span></a>
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPhoto} />
                    {thumbs.length ? <div className="fo-photos">{thumbs.map((t, i) => <img key={i} src={t} alt={'Visit photo ' + (i + 1)} className="fo-photo fo-photo--img" />)}</div> : <PhotoStrip count={visit.photos} />}
                </Section>

                {(visit.retIds || []).length ? <Section title="Returns from this visit"><ReturnsMini ids={visit.retIds} state={state} /></Section> : null}

                <Section title="Result of the visit">
                    {order ? <Banner tone="ok" title="Order taken.">{order.soNo || 'Saved on this phone'} · {money2(order.total)}</Banner> : (
                        <React.Fragment>
                            <ChoiceCards value={result} onChange={(v) => { setResult(v); setReason('') }} options={[
                                { key: 'no_order', title: 'No order', text: 'I visited but they did not order', tone: 'amber' },
                                { key: 'closed', title: 'Store closed / nobody to attend', text: 'Could not do the visit', tone: 'gray' },
                            ]} />
                            {result === 'no_order' ? <Field label="Why no order?" error={tried && !reason ? 'Pick a reason' : ''}><ChipSelect options={NO_ORDER_REASONS} value={reason} onChange={setReason} /></Field> : null}
                            {result === 'closed' ? <Field label="What happened?" error={tried && !reason ? 'Pick a reason' : ''}><ChipSelect options={CLOSED_REASONS} value={reason} onChange={setReason} /></Field> : null}
                            {!result ? <div className="fo-muted">Create an order above, or choose why there is no order.</div> : null}
                        </React.Fragment>
                    )}
                    <Field label="Visit notes (optional)"><TextArea value={notes} onChange={setNotes} rows={2} placeholder="Anything the office or your next visit should know" /></Field>
                </Section>

                {snapshot}
                <Section title="Nearby stores worth a stop" tight><NearbyPanel anchor={{ lat: store.lat, lng: store.lng }} limit={3} compact /></Section>
            </React.Fragment>
        )
        footer = (
            <React.Fragment>
                <FoBtn kind="ghost" onClick={() => setCancel(true)}>Cancel check-in</FoBtn>
                <FoBtn kind="success" big onClick={finish}>Finish visit</FoBtn>
            </React.Fragment>
        )
    } else {
        // done / missed (history or after the fact)
        const lines = order ? order.lines : null
        body = (
            <React.Fragment>
                {header}
                <Section title="Summary" right={status === 'done' ? <Pill tone={VISIT_TONE[visit.outcome] || 'green'}>{VISIT_LABEL[visit.outcome] || 'Done'}</Pill> : <Pill tone="red">{visit.skipped ? 'Skipped' : 'Missed'}</Pill>}>
                    <KV label="Date">{fmtDate(visit.date)}</KV>
                    {status === 'done' ? <React.Fragment>
                        <KV label="Checked in">{visit.checkInAt ? clockOfStamp(visit.checkInAt) : visit.time || '—'}</KV>
                        <KV label="Checked out">{visit.checkOutAt ? clockOfStamp(visit.checkOutAt) : '—'}</KV>
                        <KV label="Time on site">{visit.dur ? minutesLabel(visit.dur) : '—'}</KV>
                        {visit.reason ? <KV label="Reason">{visit.reason}</KV> : null}
                        {visit.distMi !== null && visit.distMi !== undefined ? <KV label="Distance at check-in">{visit.far ? <Pill tone="amber">{fmtMiles(visit.distMi)} away{visit.farReason ? ' · ' + visit.farReason : ''}</Pill> : 'At the store'}</KV> : null}
                        {visit.manual ? <KV label="Check-in">Manual (no location)</KV> : null}
                    </React.Fragment> : <KV label="Reason">{visit.missReason || '—'}</KV>}
                    {visit.notes ? <div className="fo-note">{visit.notes}</div> : null}
                    <PhotoStrip count={visit.photos} />
                </Section>
                {order ? <Section title={'Order ' + (order.soNo || '(pending sync)')} right={<strong>{money2(order.total)}</strong>}><LinesTable lines={lines} /></Section> : null}
                {(visit.retIds || []).length ? <Section title="Returns"><ReturnsMini ids={visit.retIds} state={state} /></Section> : null}
                {snapshot}
            </React.Fragment>
        )
        footer = <FoBtn kind="ghost" onClick={nav.pop}>Back</FoBtn>
    }

    return (
        <ScreenShell title={title} subtitle={subtitle} onBack={nav.pop} footer={saved ? null : footer}>
            {body}

            <ConfirmSheet show={cancel} title="Cancel this check-in?" text="Use this if you checked in by mistake. The visit goes back to “to visit” and nothing is recorded." confirmLabel="Cancel check-in" tone="danger" noteLabel={null} onClose={() => setCancel(false)}
                onConfirm={() => { f.dispatch({ type: 'VISIT_CANCEL_CHECKIN', visitId: visit.id }); setCancel(false); f.toast('Check-in cancelled.', 'warn') }} />

            <Sheet show={!!far} onClose={() => setFar(null)} title="You are far from the store" subtitle={far ? 'About ' + fmtMiles(far.prox.distMi) + ' away' : ''}>
                <FarForm title="Check in anyway" reasons={FAR_REASONS} onCancel={() => setFar(null)} onConfirm={(r) => commitCheckIn(far.pos, { far: true, distMi: far.prox.distMi, farReason: r })} />
            </Sheet>
            <Sheet show={manual} onClose={() => setManual(false)} title="Check in without location" subtitle="The office will see it as a manual check-in">
                <FarForm title="Check in manually" reasons={MANUAL_REASONS} onCancel={() => setManual(false)} onConfirm={(r) => commitCheckIn(null, { manual: true, farReason: r })} />
            </Sheet>
            <NoteSheet show={noteOpen} onClose={() => setNoteOpen(false)} onSave={(text) => { f.dispatch({ type: 'NOTE_ADD', storeId: store.id, text: text, by: actor.name }); setNoteOpen(false); f.toast('Note saved for this store.') }} />

            <Sheet show={saved} onClose={() => { setSaved(false); nav.pop() }} title="Visit saved ✓" subtitle={store.name + ' · ' + (effectiveResult === 'order' ? 'Order taken' : effectiveResult === 'closed' ? 'Store closed' : 'No order')}
                footer={<React.Fragment>
                    <FoBtn kind="ghost" onClick={() => { setSaved(false); nav.pop() }}>Back to my route</FoBtn>
                    {nextAfter ? <FoBtn kind="primary" icon={<FaDirections />} href={mapsNavigateUrl(nextAfter.lat, nextAfter.lng)} newTab>Navigate to next</FoBtn> : null}
                </React.Fragment>}>
                {nextAfter ? <Banner tone="info" title={'Next: #' + nextAfter.num + ' ' + nextAfter.name + '.'}>{nextAfter.eta ? fmtMiles(roadMiles(store, nextAfter)) + ' · ' + fmtMinutes(driveMinutes(roadMiles(store, nextAfter))) + ' from here.' : ''}</Banner> : <Banner tone="ok" title="That was your last stop.">You can close the day from the Route tab.</Banner>}
                <h4 className="fo-sheet__h4">Close by? Stores you could add</h4>
                <NearbyPanel anchor={{ lat: store.lat, lng: store.lng }} limit={4} compact />
            </Sheet>
        </ScreenShell>
    )
}

const fmtEtaClock = (min) => {
    const h = Math.floor(min / 60); const m = Math.round(min) % 60
    return (h % 12 === 0 ? 12 : h % 12) + ':' + (m < 10 ? '0' : '') + m + ' ' + (h < 12 ? 'AM' : 'PM')
}

const NoteSheet = ({ show, onClose, onSave }) => {
    const [t, setT] = useState('')
    useEffect(() => { if (show) setT('') }, [show])
    return (
        <Sheet show={show} onClose={onClose} title="Store note" subtitle="Everyone who visits this store will see it"
            footer={<React.Fragment><FoBtn kind="ghost" onClick={onClose}>Cancel</FoBtn><FoBtn kind="primary" disabled={!t.trim()} onClick={() => onSave(t.trim())}>Save note</FoBtn></React.Fragment>}>
            <TextArea value={t} onChange={setT} rows={4} placeholder="e.g. Ask for Mrs. Lopez · back door delivery · prefers mornings" />
        </Sheet>
    )
}
